import { DataPanel } from "react-migration/components/DataPanel";
import { TrafficDetailCard, TrafficDetailSection } from "./TrafficCountCards";
import { Tag, TagColor } from "react-migration/components/Tag";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { TrafficCountChart } from "./TrafficCountChart";
import { useTrafficCounts } from "../useTrafficCounts";
import { formatNumber } from "../util";
import { convertToTimeSeries } from "../chart";
import clsx from "clsx";
import { Icon } from "react-migration/components/Icon";
import { useTrafficMetadata } from "../useTrafficMetadata";
import { VEHICLE_TYPES, VEHICLE_TYPES_COLORS, VehicleType } from "../constants";
import { useState } from "react";
import { Sparkline } from "react-migration/components/Sparkline/Sparkline";
import { Column, Table } from "react-migration/components/TableDeprecated";
import { Hatch } from "react-migration/domains/constraints/components/Hatch/Hatch";

const NearestTrafficCountChangePercentageTag = ({
  nearestTrafficCountChangePercentage,
}: {
  nearestTrafficCountChangePercentage: number;
}) => {
  const changeDirection = nearestTrafficCountChangePercentage > 0 ? "up" : "down";

  return (
    <span
      className={clsx(
        changeDirection === "up" ? "text-green-600" : "text-red-600",
        "flex",
        "flex-row",
        "items-baseline",
        "justify-center"
      )}
    >
      {Math.abs(nearestTrafficCountChangePercentage)}%{" "}
      <Icon
        size="sm"
        icon={changeDirection === "up" ? "icon-lt-arrow-up-line" : "icon-lt-arrow-down-line"}
        extraClasses={clsx(changeDirection === "up" ? "text-green-600" : "text-red-600")}
      />
    </span>
  );
};

export const TrafficCountPanel = ({ designationData }: { designationData: SingleDesignation }) => {
  const { t } = useTranslation();
  const { roadName, roadCategory, estimationMethod } = useTrafficMetadata(designationData);
  const { yearlyTrafficData, latestTrafficData } = useTrafficCounts(designationData);
  const timeSeries = convertToTimeSeries(yearlyTrafficData, {
    seriesKeys: VEHICLE_TYPES,
    xAccessor: (d) => d.year,
    yAccessor: (d, key) => d.vehicles[key as keyof typeof d.vehicles] || 0,
  });

  const [activeCategory, setActiveCategory] = useState<VehicleType | undefined>();

  const categoryChanging = (categoryKey: VehicleType | undefined) => {
    setActiveCategory(categoryKey);
  };

  return (
    <TrafficDetailSection className="p-2 gap-y-4">
      <DataPanel.Grid columns={2} className="w-full">
        <TrafficDetailCard
          title="Nearest road name"
          tooltip={t("sidebar.selection.traffic_counts.nearest_road_name_tooltip")}
          auxiliaryData={roadCategory}
        >
          {roadName}
        </TrafficDetailCard>
        <TrafficDetailCard
          title="Nearest traffic count"
          tooltip={t("sidebar.selection.traffic_counts.nearest_traffic_count_tooltip")}
          auxiliaryData={<Tag title="Nearest traffic count" color={TagColor.DEFAULT} tag={estimationMethod} />}
          auxiliaryDataSubordinate={
            <NearestTrafficCountChangePercentageTag
              nearestTrafficCountChangePercentage={latestTrafficData?.percentageChange || 0}
            />
          }
        >
          {formatNumber(Number(latestTrafficData?.total || 0))}
        </TrafficDetailCard>
      </DataPanel.Grid>

      <DataPanel.Grid columns={1} className="w-full">
        <TrafficDetailCard title={"Latest vehicle count"}>
          <div className="gap-y-2 pt-[10px] flex flex-col w-full">
            <div className="flex flex-col w-full gap-y-2">
              <Sparkline
                categories={Object.entries(latestTrafficData?.vehicles || {})
                  .filter(([type]) => type !== "all_motor_vehicles")
                  .map(([id, value]) => ({
                    categoryKey: id,
                    name: t(`sidebar.selection.traffic_counts.vehicle_types.${id}`),
                    color: VEHICLE_TYPES_COLORS[id as keyof typeof VEHICLE_TYPES_COLORS],
                    percentage: (value / (latestTrafficData?.total || 1)) * 100,
                  }))}
                hoveredCategoryKey={activeCategory}
              />
              <Table
                primaryKey="categoryKey"
                columns={
                  [
                    {
                      label: "Category",
                      grow: true,
                      render: ({ color, name }) => (
                        <div className="flex gap-2">
                          <Hatch borderColor={color} fillColor={color} />
                          <span>{name}</span>
                        </div>
                      ),
                    },
                    {
                      label: "Count",
                      render: ({ value }) => <span>{formatNumber(value)}</span>,
                    },
                  ] as Column<{
                    categoryKey: VehicleType;
                    name: string;
                    color: string;
                    value: number;
                    percentage: number;
                  }>[]
                }
                rows={Object.entries(latestTrafficData?.vehicles || {})
                  .filter(([type]) => type !== "all_motor_vehicles")
                  .map(([id, value]) => ({
                    categoryKey: id as VehicleType,
                    name: t(`sidebar.selection.traffic_counts.vehicle_types.${id}`),
                    color: VEHICLE_TYPES_COLORS[id as keyof typeof VEHICLE_TYPES_COLORS],
                    value: value,
                    percentage: (value / (latestTrafficData?.total || 1)) * 100,
                  }))}
                size="small"
                onRowHover={(row) => categoryChanging(row?.categoryKey)}
              />
            </div>
          </div>
        </TrafficDetailCard>
      </DataPanel.Grid>

      <DataPanel.Grid columns={1} className="w-full">
        <TrafficDetailCard title={"Change over time"}>
          <div className="flex flex-col items-start justify-between gap-y-[2px] pt-[10px]">
            <TrafficCountChart data={timeSeries} />
          </div>
        </TrafficDetailCard>
      </DataPanel.Grid>
    </TrafficDetailSection>
  );
};
